import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {  MapPin, Star, Calendar, Users, Search, 
  Map as MapIcon, Grid, Filter, Navigation, Heart 
} from 'lucide-react';
import { getVenues } from '../services/api';
import { useSportsLang } from '../contexts/SportsLangContext';

// === CORES & TEMA ===
const THEME = {
  primary: '#111111',
  gold: '#b48c2c',
  bg: '#f9fafb',
  white: '#ffffff',
  text: '#1f2937',
  textLight: '#6b7280'
};

const BORDER = '#E5E7EB';

const LABELS = {
  pt: {
    title: 'Destinos Esportivos',
    subtitle: 'Descubra arenas, estádios e locais icônicos',
    searchPlaceholder: 'Buscar arena, cidade...',
    filterAll: 'Todos',
    filterStadium: 'Estádios',
    filterArena: 'Arenas Indoor',
    filterAquatic: 'Aquáticos',
    checkins: 'check-ins',
    viewDetails: 'Ver Detalhes',
    foundCount: (n) => `Encontramos ${n} locais para você explorar`,
    mapTitle: 'Mapa Interativo',
    mapDesc: (n) => `Em breve você poderá explorar ${n} locais diretamente no mapa.`,
    backToList: 'Voltar para Lista',
    emptyTitle: 'Nenhum local encontrado',
    emptyDesc: 'Tente buscar por outra cidade ou limpar os filtros.',
    showAll: 'Ver todos os locais',
    nextGame: 'Próximo jogo: Domingo, 16h',
    available: 'Disponível para eventos',
  },
  en: {
    title: 'Sports Destinations',
    subtitle: 'Discover iconic arenas, stadiums and venues',
    searchPlaceholder: 'Search arena, city...',
    filterAll: 'All',
    filterStadium: 'Stadiums',
    filterArena: 'Indoor Arenas',
    filterAquatic: 'Aquatic Centers',
    checkins: 'check-ins',
    viewDetails: 'View Details',
    foundCount: (n) => `Found ${n} venues for you to explore`,
    mapTitle: 'Interactive Map',
    mapDesc: (n) => `Soon you\'ll be able to explore ${n} venues directly on the map.`,
    backToList: 'Back to List',
    emptyTitle: 'No venues found',
    emptyDesc: 'Try searching for another city or clear the filters.',
    showAll: 'Show all venues',
    nextGame: 'Next game: Sunday, 4 PM',
    available: 'Available for events',
  },
};

// UI Primitives (padrão do site)
const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

// === HELPER: TRATAMENTO DE IMAGEM (Mantendo a lógica robusta original) ===
const getPhotoFileName = (photo) => {
  if (!photo) return null;
  try {
    let fileName = photo.split('/').pop().split('\\').pop();
    const nameMap = {
      'arenamrv.jpg': 'arena-mrv1.jpg',
      'arenamrv1.jpg': 'arena-mrv1.jpg',
      'arenamrv-1.jpg': 'arena-mrv1.jpg',
      'arena-mrv-1.jpg': 'arena-mrv1.jpg',
      'independencia.jpg': 'arena-independencia1.jpg',
      'independencia1.jpg': 'arena-independencia1.jpg',
      'independencia-1.jpg': 'arena-independencia1.jpg',
      'arena-independencia-1.jpg': 'arena-independencia1.jpg',
      'unibh.jpg': 'arena-unibh-minas1.jpg',
      'unibh1.jpg': 'arena-unibh-minas1.jpg',
      'arena-unibh-1.jpg': 'arena-unibh-minas1.jpg',
      'arena-unibh1.jpg': 'arena-unibh-minas1.jpg',
      'mineirao.jpg': 'mineirao1.jpg',
      'mineirao1.jpg': 'mineirao1.jpg',
      'mineirao-1.jpg': 'mineirao1.jpg',
      'parqueaquaticomineiras1.jpg': 'minasparqueaquatico1.jpg',
      'parqueaquaticopaineiras1.jpg': 'parqueaquaticopaineiras1.jpg',
      'parqueaquaticopaineiras1': 'parqueaquaticopaineiras1.jpg',
      'polisportivoriacho1.jpg': 'polisportivoriacho1.jpeg',
      'polisportivoriacho1': 'polisportivoriacho1.jpeg',
      'polisportivoriacho1.jpeg': 'polisportivoriacho1.jpeg'
    };
    
    fileName = fileName.replace(/-(\d)/, '$1');
    if (nameMap[fileName]) fileName = nameMap[fileName];
    else fileName = fileName.toLowerCase();
    
    if (!/\.(jpg|jpeg|webp)$/i.test(fileName)) fileName = `${fileName}.jpg`;
    return fileName;
  } catch {
    return photo;
  }
};

// === COMPONENTE: CARD DE LOCAL (SOCIAL STYLE) ===
const SocialVenueCard = ({ venue }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const photoName = getPhotoFileName(venue.mainPhoto);
  const imageSrc = photoName ? `/arenas/${photoName}` : null;
  
  const rating = (Math.random() * (5.0 - 3.5) + 3.5).toFixed(1);
  const checkins = Math.floor(Math.random() * 5000) + 100;
  const nextEvent = venue.tenantTeamsCount > 0 ? t.nextGame : t.available;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col overflow-hidden h-full">
      <div className="h-48 relative overflow-hidden bg-gray-100">
        {imageSrc ? (
          <img 
            src={imageSrc} 
            alt={venue.name} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        
        <div 
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#111111] to-[#0b0b0c]"
          style={{ display: imageSrc ? 'none' : 'flex' }}
        >
          <div className="text-center text-white p-4">
            <h3 className="text-xl font-bold opacity-90">{venue.name}</h3>
            <p className="text-xs opacity-75 mt-1 uppercase tracking-widest">{venue.type}</p>
          </div>
        </div>

        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-md text-xs font-bold text-gray-800 shadow-sm uppercase tracking-wide">
          {venue.type === 'parque-aquatico' ? 'Aquático' : venue.type}
        </div>

        <button className="absolute top-3 right-3 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors backdrop-blur-sm">
          <Heart size={16} />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-bold text-gray-900 line-clamp-1 group-hover:text-[#111111] transition-colors">
            <Link to={`/locais/${venue.venueId || venue.id}`}>{venue.name}</Link>
          </h3>
          <div className="flex items-center gap-1 text-yellow-500 text-xs font-bold bg-yellow-50 px-1.5 py-0.5 rounded">
            <Star size={12} fill="currentColor" />
            <span>{rating}</span>
          </div>
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
          <MapPin size={12} />
          <span className="truncate">{venue.city}, {venue.country}</span>
        </div>

        <div className="mt-auto pt-3 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span className="flex items-center gap-1.5">
              <Users size={12} className="text-[#111111]" />
              <strong>{checkins}</strong> {t.checkins}
            </span>
            <span className="flex items-center gap-1.5 truncate">
              <Calendar size={12} className="text-[#b48c2c]" />
              <span className="truncate">{nextEvent}</span>
            </span>
          </div>

          <Link 
            to={`/locais/${venue.venueId || venue.id}`}
            className="w-full mt-2 py-2 rounded-lg bg-gray-50 text-gray-700 text-sm font-bold hover:bg-[#111111] hover:text-white transition-all flex items-center justify-center gap-2 border border-gray-200 hover:border-transparent"
          >
            {t.viewDetails}
          </Link>
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE PRINCIPAL ===
const VenuesPage = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [venues, setVenues] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const loadVenues = async () => {
      try {
        setLoading(true);
        const response = await getVenues();
        if (response.data?.success) {
          setVenues(response.data.venues);
          setFilteredVenues(response.data.venues);
        }
      } catch (err) {
        console.error("Erro ao carregar locais", err);
      } finally {
        setLoading(false);
      }
    };
    loadVenues();
  }, []);

  useEffect(() => {
    let result = venues;

    if (activeFilter !== 'ALL') {
      const typeMap = {
        'STADIUM': ['estadio', 'stadium'],
        'ARENA': ['arena', 'ginasio'],
        'AQUATIC': ['parque-aquatico', 'aquatic-center']
      };
      result = result.filter(v => typeMap[activeFilter]?.includes(v.type));
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(v => 
        v.name.toLowerCase().includes(term) || 
        v.city.toLowerCase().includes(term)
      );
    }

    setFilteredVenues(result);
  }, [searchTerm, activeFilter, venues]);

  return (
    <PageShell>
      
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          
          {/* Header Padronizado com Logo */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl overflow-hidden flex items-center justify-center bg-white border-2" 
                 style={{ borderColor: THEME.gold }}>
              <img 
                src="/vukz-logo.jpeg" 
                alt="Arena Stats" 
                className="w-full h-full object-contain p-1"
              />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl lg:text-3xl font-extrabold" style={{ color: THEME.primary }}>
                {t.title}
              </h1>
              <p className="text-gray-600 text-sm lg:text-base">
                {t.subtitle}
              </p>
            </div>
            <div className="relative w-full md:w-72">
              <input 
                type="text" 
                placeholder={t.searchPlaceholder} 
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#111111] focus:bg-white transition-all text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            
            <div className="flex gap-2 overflow-x-auto pb-1 w-full sm:w-auto scrollbar-hide">
              {[
                { id: 'ALL', label: t.filterAll },
                { id: 'STADIUM', label: t.filterStadium, icon: '🏙️' },
                { id: 'ARENA', label: t.filterArena, icon: '🏀' },
                { id: 'AQUATIC', label: t.filterAquatic, icon: '🏄' },
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all border ${
                    activeFilter === filter.id 
                      ? 'bg-[#111111] text-white border-[#111111]' 
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {filter.icon} {filter.label}
                </button>
              ))}
            </div>

            <div className="flex bg-gray-100 p-1 rounded-lg shrink-0">
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow text-[#111111]' : 'text-gray-500'}`}
              >
                <Grid size={18} />
              </button>
              <button 
                onClick={() => setViewMode('map')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'map' ? 'bg-white shadow text-[#111111]' : 'text-gray-500'}`}
              >
                <MapIcon size={18} />
              </button>
            </div>

          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-white h-80 rounded-xl border border-gray-200 animate-pulse"></div>
            ))}
          </div>
        ) : filteredVenues.length > 0 ? (
          
          viewMode === 'grid' ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                {t.foundCount(filteredVenues.length)}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredVenues.map((venue) => (
                  <SocialVenueCard key={venue.id} venue={venue} />
                ))}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-[600px] relative flex flex-col items-center justify-center text-center p-8">
               <div className="absolute inset-0 bg-gray-100 opacity-50 bg-[url('https://upload.wikimedia.org/wikipedia/commons/e/ec/World_map_blank_without_borders.svg')] bg-cover bg-center"></div>
               <div className="relative z-10 bg-white p-8 rounded-2xl shadow-xl max-w-md">
                 <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-[#111111]">
                   <Navigation size={32} />
                 </div>
                 <h3 className="text-xl font-bold text-gray-900 mb-2">{t.mapTitle}</h3>
                 <p className="text-gray-600 mb-6">
                   {t.mapDesc(filteredVenues.length)}
                 </p>
                 <button 
                   onClick={() => setViewMode('grid')}
                   className="px-6 py-2 bg-[#111111] text-white rounded-lg font-bold hover:bg-[#0b0b0c]"
                 >
                   {t.backToList}
                 </button>
               </div>
            </div>
          )

        ) : (
          <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900">{t.emptyTitle}</h3>
            <p className="text-gray-500">{t.emptyDesc}</p>
            <button 
              onClick={() => { setSearchTerm(''); setActiveFilter('ALL'); }}
              className="mt-4 text-[#111111] font-bold hover:underline"
            >
              {t.showAll}
            </button>
          </div>
        )}

      </div>
    </PageShell>
  );
};

export default VenuesPage;
